import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Backdrop,
  SvgIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { ReactComponent as deleteLogo } from '@/assets/icons/delete.svg';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { DefectTableFeature } from '@/enums/DefectTableFeatuer.enum';
import { displayedDefectField, getDefectSeverity, getDivision } from '@/utils/inspection.utils';
import { DefectSeverity as DefectSeverityEnum } from '@/enums/DefectSeverity.enum';
import { InspectionDefect, InspectionDefectView } from '@/types/InspectionDefect';
import { toDefectView, toDefectStorage } from '@/utils/inspectionDefect.utils';
import { DefectDetails } from '@/features/components/DefectDetails/DefectDetails';
import { ReactComponent as DefectDetailsIcon } from '@/assets/icons/defect_details.svg';
import { DefectCode, DefectSeverity } from '@/types/DefectCategory.type';
import { Image } from '@/types/Image.type';
import { generateUniqueId } from '@/utils/string.util';
import { RootState } from '@/store/rootReducer';
import AddButton from '@/components/AddButton';
import { Mode } from '@/enums/Mode.enum';
import { DefectImage, buildDefectImageSlides } from './DefectImage';
import { AppConstants } from '@/app.constants';
import { findCriticalSeverity, QUANTITY_FAIL_DEFECT_KEY } from '@/utils/quantity-check.utils';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import {
  ImageZoomLightbox,
  type ImageZoomSlide,
} from '@/features/components/ImageCard/ImageZoomLightbox';

import styles from './DefectList.module.scss';

export const DefectList: React.FC<DynamicComponentProps> = ({
  inspection,
  structure,
  defectsChangeHandlers: defectsChangeHandler,
}: DynamicComponentProps) => {
  const { severities, divisions } = useSelector((state: RootState) => state.masterData || {});
  const config = useSelector((state: RootState) => state.appConfig || {});
  const initialDefect: InspectionDefectView = {
    id: '',
    inspectionDataId: inspection?.id || '',
    defectId: '',
    defectSeverity: DefectSeverityEnum.MAJOR,
    defectSeverityId:
      severities.find((s: DefectSeverity) => s.name === DefectSeverityEnum.MAJOR)?.id || '',
    defectQuantity: 1,
    comment: '',
    defectCode: {
      id: '',
      code: '',
      codeDescription: '',
      categoryDescription: '',
    },
    existingImages: [],
  };
  const { id, moduleName } = useParams();
  const qcDefects =
    (moduleName
      ? inspection?.qcDefects?.filter((d) => d.module === moduleName)
      : inspection?.qcDefects) || [];
  const [defectViewMode, setDefectViewMode] = useState<Mode>(Mode.ADD);
  const { t: translate, i18n } = useTranslation();
  const [showDefectDetails, setShowDefectDetails] = useState<boolean>(false);
  const shouldShowDelete = structure?.features?.some(
    (feature) => feature === DefectTableFeature.DELETE
  );
  const shouldShowAdd = structure?.features?.some((feature) => feature === DefectTableFeature.ADD);
  const [currentDefect, setCurrentDefect] = useState<InspectionDefectView>();
  const [isSeverityLocked, setIsSeverityLocked] = useState(false);
  const [defectTableExpanded, setDefectTableExpanded] = useState<boolean>(
    () => sessionStorage.getItem('defectListExpanded') !== 'false'
  );
  const [fullView, setFullView] = useState<boolean>(false);
  const [defectGalleryOpen, setDefectGalleryOpen] = useState(false);
  const [defectGalleryInitialIndex, setDefectGalleryInitialIndex] = useState(0);

  const defectGalleryData = useMemo(() => {
    const slides: ImageZoomSlide[] = [];
    const startByRow = new Map<string, number>();
    for (const d of qcDefects) {
      const rowKey = String(d.newId || d.id || '');
      const title =
        displayedDefectField(d.defectCode, 'codeDescription', i18n.language) ||
        translate('defect_images');
      const part = buildDefectImageSlides(d, title);
      if (part.length > 0) {
        startByRow.set(rowKey, slides.length);
        slides.push(...part);
      }
    }
    return { slides, startByRow };
  }, [qcDefects, translate, i18n.language]);

  useEffect(() => {
    const handleResize = () => {
      setFullView(window.innerWidth > 600); // threshold for switching view
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (defectsChangeHandler?.isAddDefectVisible === true) {
      handleAddClick();
    } else if (defectsChangeHandler?.isAddDefectVisible === false) {
      handleDefectCancel();
    }
  }, [defectsChangeHandler?.isAddDefectVisible]);

  const openDefectDetails = (defect?: InspectionDefectView) => {
    setCurrentDefect(defect || initialDefect);
    setShowDefectDetails(true);
  };
  const handleAddClick = () => {
    const isQuantityFailDefect = sessionStorage.getItem(QUANTITY_FAIL_DEFECT_KEY) === 'true';
    sessionStorage.removeItem(QUANTITY_FAIL_DEFECT_KEY);
    const lockedSeverity = isQuantityFailDefect ? findCriticalSeverity(severities) : undefined;
    const newDefect = {
      ...initialDefect,
      newId: generateUniqueId(),
      ...(lockedSeverity
        ? { defectSeverity: lockedSeverity.name, defectSeverityId: lockedSeverity.id }
        : {}),
    };
    setIsSeverityLocked(lockedSeverity != null);
    setDefectViewMode(Mode.ADD);
    openDefectDetails(newDefect);
  };
  const handleDefectRowClick = (defect: InspectionDefect): void => {
    setIsSeverityLocked(false);
    setDefectViewMode(Mode.EDIT);
    openDefectDetails(
      toDefectView({ ...defect, newId: defect.id || defect.newId || generateUniqueId() })
    );
  };

  const handleDeleteClick = (defect: InspectionDefect): void => {
    if (!defectsChangeHandler?.onDelete) {
      return;
    }
    const identity = defect.id?.trim() || defect.newId?.trim();
    if (!identity) {
      return;
    }
    const sourceDefect =
      inspection?.qcDefects?.find((candidate) => {
        const candidateId = candidate.id?.trim();
        const candidateNewId = candidate.newId?.trim();
        return candidateId === identity || candidateNewId === identity;
      }) || defect;
    defectsChangeHandler.onDelete(sourceDefect);
  };
  const handleDefectSave = () => {
    if (currentDefect) {
      const storage = toDefectStorage(currentDefect);
      if (defectViewMode === Mode.ADD && defectsChangeHandler?.onAdd) {
        defectsChangeHandler.onAdd({ ...storage, module: moduleName || '' });
      } else if (defectViewMode === Mode.EDIT && defectsChangeHandler?.onEdit) {
        defectsChangeHandler.onEdit({
          ...storage,
          module: currentDefect.module || AppConstants.DEFAULT_MODULE,
        });
      }
    }
    setShowDefectDetails(false);
  };

  const handleDefectCancel = (): void => {
    setCurrentDefect(initialDefect);
    if (defectsChangeHandler?.isAddDefectVisible === true) {
      defectsChangeHandler?.showAddDefect?.(false);
    }
    setShowDefectDetails(false);
  };

  const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setDefectTableExpanded(isExpanded);
    sessionStorage.setItem('defectListExpanded', String(isExpanded));
  };

  if (!id) {
    return <p>Missing inspection or module</p>;
  }

  const handleCodeChange = (code: DefectCode) => {
    if (currentDefect) {
      setCurrentDefect({ ...currentDefect, defectCode: code, defectId: code.id });
    }
  };
  const handleSeverityChange = (severity: DefectSeverity) => {
    if (currentDefect) {
      setCurrentDefect({
        ...currentDefect,
        defectSeverity: severity.name,
        defectSeverityId: severity.id,
      });
    }
  };
  const handleQuantityChange = (quantity: number) => {
    if (currentDefect) {
      setCurrentDefect({ ...currentDefect, defectQuantity: quantity });
    }
  };
  const handleCommentsChange = (comments: string) => {
    if (currentDefect) {
      setCurrentDefect({ ...currentDefect, comment: comments });
    }
  };
  const handleImageAdd = (image: Image) => {
    if (currentDefect) {
      setCurrentDefect({
        ...currentDefect,
        newImages: [...(currentDefect.newImages || []), image],
      });
    }
  };
  const handleImageRemove = (image: Image) => {
    if (currentDefect) {
      if (image.isNew) {
        const updatedImages = currentDefect.newImages?.map((i) => {
          if (i.id === image.id) {
            return { ...i, isDeleted: true };
          }
          return i;
        });
        setCurrentDefect({ ...currentDefect, newImages: updatedImages });
      } else {
        const existingImages = (currentDefect.existingImages || []).filter(
          (i) => i.url !== image.url
        );
        setCurrentDefect({ ...currentDefect, existingImages });
      }
    }
  };

  const divisionId = getDivision(inspection, config, divisions);

  const { criticalCount, majorCount, minorCount, totalDefectCount } = useMemo(() => {
    const critical = qcDefects
      .filter((d) => d.defectSeverity === DefectSeverityEnum.CRITICAL)
      .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
    const major = qcDefects
      .filter((d) => d.defectSeverity === DefectSeverityEnum.MAJOR)
      .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
    const minor = qcDefects
      .filter((d) => d.defectSeverity === DefectSeverityEnum.MINOR)
      .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
    return {
      criticalCount: critical,
      majorCount: major,
      minorCount: minor,
      totalDefectCount: critical + major + minor,
    };
  }, [qcDefects]);

  const valueChangeHandlers = {
    onSave: handleDefectSave,
    onCancel: handleDefectCancel,
    onDelete: handleDeleteClick,
    onCodeChange: handleCodeChange,
    onSeverityChange: handleSeverityChange,
    onQuantityChange: handleQuantityChange,
    onCommentsChange: handleCommentsChange,
    onImageAdd: handleImageAdd,
    onImageRemove: handleImageRemove,
  };
  const actionHandlers = {
    onSave: handleDefectSave,
    onCancel: handleDefectCancel,
    onDelete: handleDeleteClick,
  };

  return (
    <>
      <div className={styles.tableContainer}>
        <Accordion
          expanded={defectTableExpanded}
          onChange={handleAccordionChange}
          sx={{
            width: '100%',
            boxShadow: 'none',
            '&.Mui-expanded': {
              margin: 0, // or any other value you want
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3-content"
            id="panel3-header"
            className={styles.summary}
            sx={{
              flexDirection: 'column',
              '& .MuiAccordionSummary-content': {
                marginBottom: 0,
                width: '100%',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                position: 'absolute',
                right: 16,
                top: 'calc(50% - 12px)',
              },
            }}
          >
            <h2
              className={styles.header}
              style={{
                borderBottom: defectTableExpanded ? '.06rem solid #A7A7A7' : 'none',
                display: 'flex',
                alignItems: 'center',
                paddingBottom: defectTableExpanded ? '0.5rem' : '0',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 1.67rem 0 .67rem',
                  borderRight: '.11rem solid #e0e0e0',
                }}
              >
                <DefectDetailsIcon style={{ width: '40px', height: '40px' }} />
              </Box>
              <span style={{ flex: 1, textAlign: 'center', paddingRight: '60px' }}>
                {translate('Defect_Details')}
                {totalDefectCount > 0 && (
                  <span className={styles.count}> ({totalDefectCount})</span>
                )}
              </span>
            </h2>
          </AccordionSummary>
          <AccordionDetails className={styles.details}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{translate('Code')}</th>
                  <th className={styles.description}>{translate('Description')}</th>
                  <th>
                    {translate(fullView ? DefectSeverityEnum.CRITICAL : 'Cri')}
                    {criticalCount > 0 && <span className={styles.count}> ({criticalCount})</span>}
                  </th>
                  <th>
                    {translate(fullView ? DefectSeverityEnum.MAJOR : 'Maj')}
                    {majorCount > 0 && <span className={styles.count}> ({majorCount})</span>}
                  </th>
                  <th>
                    {translate(fullView ? DefectSeverityEnum.MINOR : 'Min')}
                    {minorCount > 0 && <span className={styles.count}> ({minorCount})</span>}
                  </th>
                  <th className={styles.defectColumn}>{translate(fullView ? 'Defect' : 'Def')}</th>
                  {shouldShowDelete && <th></th>}
                </tr>
              </thead>
              <tbody>
                {qcDefects.length === 0 ? (
                  <tr>
                    <td colSpan={6 + (shouldShowDelete ? 1 : 0)} className={styles.emptyState}>
                      {translate('No_defects_added')}
                    </td>
                  </tr>
                ) : (
                  qcDefects.map((d) => (
                    <tr key={d.newId || d.id} onClick={() => handleDefectRowClick(d)}>
                      <td>{d.defectCode?.code}</td>
                      <td className={styles.description}>
                        {displayedDefectField(d.defectCode, 'codeDescription', i18n.language)}
                      </td>
                      <td>{getDefectSeverity(d, DefectSeverityEnum.CRITICAL)}</td>
                      <td>{getDefectSeverity(d, DefectSeverityEnum.MAJOR)}</td>
                      <td>{getDefectSeverity(d, DefectSeverityEnum.MINOR)}</td>
                      <td className={`${styles.defectImage} ${styles.defectColumn}`}>
                        <DefectImage
                          defect={d}
                          inspectionId={id!}
                          zoomTitle={displayedDefectField(
                            d.defectCode,
                            'codeDescription',
                            i18n.language
                          )}
                          sharedGalleryStartIndex={
                            defectGalleryData.startByRow.get(String(d.newId || d.id || '')) ?? 0
                          }
                          onOpenSharedGallery={(start) => {
                            setDefectGalleryInitialIndex(start);
                            setDefectGalleryOpen(true);
                          }}
                        />
                      </td>
                      {shouldShowDelete && (
                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(d);
                          }}
                        >
                          <button
                            type="button"
                            className={styles.deleteButton}
                            aria-label={translate('Delete')}
                          >
                            <SvgIcon
                              component={deleteLogo}
                              sx={{ fill: 'none', height: '1.67rem' }}
                            />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AccordionDetails>
        </Accordion>
        {shouldShowAdd && (
          <AddButton onClick={handleAddClick} withContainer>
            {translate('Add_Plus')}
          </AddButton>
        )}
      </div>

      {defectGalleryOpen && defectGalleryData.slides.length > 0 && id && (
        <ImageZoomLightbox
          key={`defect-gallery-${defectGalleryInitialIndex}`}
          inspectionId={id}
          inspectionImageType={InspectionImageType.DEFECT}
          slides={defectGalleryData.slides}
          initialIndex={defectGalleryInitialIndex}
          onClose={() => setDefectGalleryOpen(false)}
        />
      )}

      {showDefectDetails && (
        <Backdrop
          open={showDefectDetails}
          className={styles.defectDetailsBackdrop}
          sx={(theme) => ({
            zIndex: theme.zIndex.modal + 1,
          })}
        >
          <div onClick={(e) => e.stopPropagation()} className={styles.defectDetailsDialog}>
            <DefectDetails
              mode={defectViewMode}
              defect={currentDefect}
              inspectionId={id}
              divisionId={divisionId}
              isSeverityHidden={isSeverityLocked}
              valueChangeHandlers={valueChangeHandlers}
              actionHandlers={actionHandlers}
            />
          </div>
        </Backdrop>
      )}
    </>
  );
};
